/**
 * URL Parameters Utility
 * 
 * Parse and extract query string parameters:
 * - Email parameter
 * - Request code (GUID) parameter
 * - Safe parsing with validation
 */

/**
 * Parse URL query parameters
 * 
 * @param {string} search - URL search string (e.g., window.location.search)
 * @returns {URLSearchParams} Parsed parameters
 */
export function parseQueryParams(search = window.location.search) {
  return new URLSearchParams(search);
}

/**
 * Get email from URL query parameters
 * 
 * @param {string} search - URL search string
 * @returns {string|null} Email address or null if not present
 */
export function getEmailParam(search = window.location.search) {
  const params = parseQueryParams(search);
  return params.get('email') || null;
}

/**
 * Get request code from URL query parameters
 * 
 * Supports both 'code' and 'requestId' parameter names
 * 
 * @param {string} search - URL search string
 * @returns {string|null} Request code (GUID) or null if not present
 */
export function getRequestCodeParam(search = window.location.search) {
  const params = parseQueryParams(search);
  return params.get('code') || params.get('requestId') || null;
}

/**
 * Get Salesforce Case Id from URL query parameters
 *
 * @param {string} search - URL search string
 * @returns {string|null} Case Id or null if not present
 */
export function getCaseIdParam(search = window.location.search) {
  const params = parseQueryParams(search);
  return params.get('caseId') || null;
}

/**
 * Check if URL has Salesforce parameters (email + caseId)
 *
 * @param {string} search - URL search string
 * @returns {boolean} True if both email and caseId parameters present
 */
export function hasSalesforceParams(search = window.location.search) {
  const params = parseQueryParams(search);
  return Boolean((params.get('email') || null) && (params.get('caseId') || null));
}

/**
 * Get all payment-related parameters from URL
 * 
 * @param {string} search - URL search string
 * @returns {{ email: string|null, requestCode: string|null }} Parameters object
 */
export function getPaymentParams(search = window.location.search) {
  return {
    email: getEmailParam(search),
    requestCode: getRequestCodeParam(search),
  };
}

/**
 * Check if URL has payment parameters
 * 
 * @param {string} search - URL search string
 * @returns {boolean} True if both email and code parameters present
 */
export function hasPaymentParams(search = window.location.search) {
  const { email, requestCode } = getPaymentParams(search);
  return Boolean(email && requestCode);
}

/**
 * Build payment URL with parameters
 * 
 * @param {string} baseUrl - Base URL
 * @param {string} email - Email address
 * @param {string} requestCode - Request code (GUID)
 * @returns {string} Complete URL with query parameters
 */
export function buildPaymentUrl(baseUrl, email, requestCode) {
  const url = new URL(baseUrl);
  url.searchParams.set('email', email);
  url.searchParams.set('code', requestCode);
  return url.toString();
}

export default {
  parseQueryParams,
  getEmailParam,
  getRequestCodeParam,
  getCaseIdParam,
  hasSalesforceParams,
  getPaymentParams,
  hasPaymentParams,
  buildPaymentUrl,
};
